import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import {
    PlusLg,
    PencilSquare,
    Trash3,
    ArrowClockwise,
    Bookmark,
} from 'react-bootstrap-icons';
import {
    listSubscriptionPlans,
    createSubscriptionPlan,
    updateSubscriptionPlan,
    deleteSubscriptionPlan,
    type SubscriptionPlanFull,
    type SubscriptionPlanCreateInput,
    type SubscriptionPlanUpdateInput,
    type FeatureFlagKey,
    type PlanType,
    type BillingInterval,
} from '../api/plans';
import CustomDropdown from '../components/CustomDropdown';
import { LabelWithInfo, PlanToggle } from '../components/PlanFormHelpers';
import {
    listSuperAdminOrganizations,
    type SuperAdminOrganization,
} from '../api/superadmin';

import './SuperAdminPlans-new.css';

const FEATURE_OPTIONS: Array<{ key: FeatureFlagKey; label: string; tooltip: string; locked?: boolean }> = [
    { key: 'testcase_generation', label: 'Test Case Generation', tooltip: 'Always enabled. Lets users generate test cases from requirements.', locked: true },
    { key: 'test_execution', label: 'Test Execution', tooltip: 'Allows running generated Playwright/Selenium scripts against environments.' },
    { key: 'synthetic_data', label: 'Synthetic Data', tooltip: 'Enables AI-driven synthetic data generation for tests.' },
    { key: 'data_validation_agent', label: 'Data Validation Agent', tooltip: 'Enables the CSV and database comparison workflow in the Data Validation feature.' },
    { key: 'byo_key', label: 'Bring Your Own Key (BYO)', tooltip: 'Lets organization admins supply their own LLM API key. In BYO mode, regular LLM usage uses the org key while vector-ingestion and embedding calls still use LLM Usage Credits.' },
];

interface FormState
{
    name: string;
    description: string;
    plan_type: PlanType;
    billing_interval: BillingInterval;
    price_dollars: string;
    user_limit: string;
    feature_flags: Set<FeatureFlagKey>;
    free_token_quota: string;
    free_quota_period_days: string;
    is_active: boolean;
    org_id: number | '';
}

const EMPTY_FORM: FormState = {
    name: '',
    description: '',
    plan_type: 'paid',
    billing_interval: 'month',
    price_dollars: '0',
    user_limit: '',
    feature_flags: new Set<FeatureFlagKey>(['testcase_generation']),
    free_token_quota: '0',
    free_quota_period_days: '30',
    is_active: true,
    org_id: '',
};

function toFormState(plan: SubscriptionPlanFull): FormState
{
    return {
        name: plan.name,
        description: plan.description ?? '',
        plan_type: plan.plan_type,
        billing_interval: plan.billing_interval,
        price_dollars: (plan.price_cents / 100).toFixed(2),
        user_limit: plan.user_limit == null ? '' : String(plan.user_limit),
        feature_flags: new Set<FeatureFlagKey>(plan.feature_flags),
        free_token_quota: plan.free_token_quota == null ? '0' : String(plan.free_token_quota),
        free_quota_period_days: plan.free_quota_period_days == null ? '30' : String(plan.free_quota_period_days),
        is_active: plan.is_active,
        org_id: plan.org_id ?? '',
    };
}

function buildCreateBody(form: FormState): SubscriptionPlanCreateInput
{
    const features = Array.from(form.feature_flags);
    const org_id = form.org_id !== '' ? form.org_id : null;
    if (form.plan_type === 'free') {
        return {
            name: form.name.trim(),
            description: form.description.trim() || null,
            plan_type: 'free',
            billing_interval: 'free',
            price_cents: 0,
            currency: 'usd',
            user_limit: form.user_limit.trim() ? Math.max(1, Math.floor(Number(form.user_limit))) : null,
            feature_flags: features,
            free_token_quota: Math.max(0, Math.floor(Number(form.free_token_quota || '0'))),
            free_quota_period_days: Math.max(1, Math.floor(Number(form.free_quota_period_days || '30'))),
            is_active: form.is_active,
            org_id,
        };
    }
    return {
        name: form.name.trim(),
        description: form.description.trim() || null,
        plan_type: 'paid',
        billing_interval: form.billing_interval === 'free' ? 'month' : form.billing_interval,
        price_cents: Math.round(Number(form.price_dollars || '0') * 100),
        currency: 'usd',
        user_limit: form.user_limit.trim() ? Math.max(1, Math.floor(Number(form.user_limit))) : null,
        feature_flags: features,
        is_active: form.is_active,
        org_id,
    };
}

function buildUpdateBody(form: FormState): SubscriptionPlanUpdateInput
{
    const base = buildCreateBody(form);
    return {
        ...base,
        user_limit_set: true,
        free_token_quota_set: form.plan_type === 'free',
        free_quota_period_days_set: form.plan_type === 'free',
        org_id: base.org_id,
        org_id_set: true,
    };
}

function SuperAdminSubscriptionPlans(): React.ReactElement
{
    const [plans, setPlans] = useState<SubscriptionPlanFull[]>([]);
    const [orgs, setOrgs] = useState<SuperAdminOrganization[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<SubscriptionPlanFull | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [subs, orgsResp] = await Promise.all([
                listSubscriptionPlans(),
                listSuperAdminOrganizations({ page: 1, page_size: 200 }),
            ]);
            setPlans(subs);
            setOrgs(orgsResp.items);
        }
        catch (exc: any) {
            setError(exc?.response?.data?.detail || exc?.message || 'Failed to load subscription plans');
        }
        finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void refresh(); }, [refresh]);

    function openCreate() {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormError(null);
        setModalOpen(true);
    }

    function openEdit(plan: SubscriptionPlanFull) {
        setEditing(plan);
        setForm(toFormState(plan));
        setFormError(null);
        setModalOpen(true);
    }

    async function handleSave(e: React.FormEvent) {
        e.preventDefault();
        setFormError(null);
        if (!form.name.trim()) { setFormError('Name is required'); return; }
        if (form.plan_type === 'paid' && Math.round(Number(form.price_dollars || '0') * 100) <= 0) {
            setFormError('Paid plans must have a recurring price greater than $0.00');
            return;
        }
        setSubmitting(true);
        try {
            if (editing) {
                await updateSubscriptionPlan(editing.id, buildUpdateBody(form));
            } else {
                await createSubscriptionPlan(buildCreateBody(form));
            }
            setModalOpen(false);
            await refresh();
        }
        catch (exc: any) {
            setFormError(exc?.response?.data?.detail || exc?.message || 'Save failed');
        }
        finally {
            setSubmitting(false);
        }
    }

    async function handleDelete(plan: SubscriptionPlanFull) {
        const confirmed = window.confirm(`Permanently delete subscription plan "${plan.name}"?`);
        if (!confirmed) return;
        setDeletingId(plan.id);
        try {
            await deleteSubscriptionPlan(plan.id);
            await refresh();
        }
        catch (exc: any) {
            window.alert(exc?.response?.data?.detail || exc?.message || 'Delete failed');
        }
        finally {
            setDeletingId(null);
        }
    }

    function toggleFeature(key: FeatureFlagKey, next: boolean) {
        const flags = new Set(form.feature_flags);
        if (next) flags.add(key); else flags.delete(key);
        flags.add('testcase_generation'); // always on
        setForm({ ...form, feature_flags: flags });
    }

    const planTypeOptions = useMemo(() => ([
        { value: 'paid', label: 'Paid' },
        { value: 'free', label: 'Free' },
    ]), []);

    const billingIntervalOptions = useMemo(() => ([
        { value: 'day', label: 'Daily' },
        { value: 'month', label: 'Monthly' },
        { value: 'year', label: 'Yearly' },
    ]), []);

    const orgOptions = useMemo(
        () => orgs.map((org) => ({
            value: String(org.id),
            label: `${org.name} (#${org.id})`,
        })),
        [orgs]
    );

    return (
        <div className="sa-plans-page">
            <div className="sa-plans-breadcrumbs">Super Admin · Subscription Plans</div>
            <div className="sa-plans-header-row">
                <div>
                    <h1 className="sa-plans-title">Subscription Plans</h1>
                    <p className="sa-plans-subtitle">
                        Configure free, daily, monthly, and yearly plans. Paid plans bill a recurring base price; platform-key LLM usage is funded by LLM Usage Credits.
                    </p>
                </div>
                <div className="sa-plans-actions">
                    <button
                        className="sa-plans-btn sa-plans-btn-icon"
                        onClick={() => void refresh()}
                        disabled={loading}
                        title="Refresh"
                    >
                        <ArrowClockwise size={14} /> Refresh
                    </button>
                    <button className="sa-plans-btn sa-plans-btn-primary sa-plans-btn-icon" onClick={openCreate}>
                        <PlusLg size={14} /> New Subscription Plan
                    </button>
                </div>
            </div>

            {error && <div className="sa-plans-error">{error}</div>}

            {loading ? (
                <div className="sa-plans-page-loading">
                    <Spinner animation="border" size="sm" /> Loading subscription plans…
                </div>
            ) : (
                <div
                    className="sa-plans-card"
                    role="region"
                    aria-label="Subscription plans"
                    tabIndex={0}
                >
                    {plans.length === 0 ? (
                        <div className="sa-plans-empty">No subscription plans yet. Create your first one.</div>
                    ) : (
                        <table className="sa-plans-table" data-responsive-cards="true">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Interval</th>
                                    <th>Price (USD)</th>
                                    <th>Usage Model</th>
                                    <th>Users</th>
                                    <th>Features</th>
                                    <th>Status</th>
                                    <th>Organization</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {plans.map((plan) => (
                                    <tr key={plan.id}>
                                        <td data-label="Name"><strong>{plan.name}</strong></td>
                                        <td data-label="Type">
                                            <span className={`sa-plans-pill ${plan.plan_type === 'free' ? 'sa-plans-pill-free' : 'sa-plans-pill-paid'}`}>
                                                {plan.plan_type}
                                            </span>
                                        </td>
                                        <td data-label="Interval">{plan.plan_type === 'free' ? '—' : plan.billing_interval}</td>
                                        <td data-label="Price (USD)">{plan.plan_type === 'free' ? '—' : `$${(plan.price_cents / 100).toFixed(2)}`}</td>
                                        <td data-label="Usage Model">
                                            {plan.plan_type === 'free'
                                                ? `${(plan.free_token_quota ?? 0).toLocaleString()} free tokens`
                                                : 'Base subscription + LLM Usage Credits'}
                                        </td>
                                        <td data-label="Users">{plan.user_limit == null ? '∞' : plan.user_limit}</td>
                                        <td className="sa-plans-cell-features" data-label="Features">
                                            {plan.feature_flags.length === 0 ? '—' : plan.feature_flags.join(', ')}
                                        </td>
                                        <td data-label="Status">
                                            {plan.is_active ? (
                                                <span className="sa-plans-pill sa-plans-pill-active">active</span>
                                            ) : (
                                                <span className="sa-plans-pill sa-plans-pill-inactive">inactive</span>
                                            )}
                                        </td>
                                        <td data-label="Organization">
                                            {plan.is_custom ? (
                                                <span className="sa-plans-pill sa-plans-pill-custom" title={`Org #${plan.org_id}`}>
                                                    {orgs.find((o) => o.id === plan.org_id)?.name ?? `Org #${plan.org_id}`}
                                                </span>
                                            ) : (
                                                <span className="sa-plans-pill sa-plans-pill-free">Global</span>
                                            )}
                                        </td>
                                        <td className="sa-plans-cell-actions" data-label="Actions" data-card-actions="true">
                                            <button
                                                className="sa-plans-icon-btn"
                                                title="Edit"
                                                onClick={() => openEdit(plan)}
                                                disabled={deletingId === plan.id}
                                            >
                                                <PencilSquare size={14} /> Edit
                                            </button>
                                            <button
                                                className="sa-plans-icon-btn sa-plans-icon-btn-danger"
                                                title="Delete"
                                                onClick={() => handleDelete(plan)}
                                                disabled={deletingId === plan.id}
                                            >
                                                {deletingId === plan.id
                                                    ? <><Spinner animation="border" size="sm" /> Deleting…</>
                                                    : <><Trash3 size={14} /> Delete</>}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {modalOpen && (
                <div className="sa-plans-modal-overlay" role="dialog" aria-modal="true">
                    <form className="sa-plans-modal" onSubmit={handleSave}>
                        <h2 className="sa-plans-modal-title">
                            <Bookmark size={18} style={{ marginRight: 8, verticalAlign: '-2px' }} />
                            {editing ? `Edit Subscription Plan #${editing.id}` : 'New Subscription Plan'}
                        </h2>
                        {formError && <div className="sa-plans-error">{formError}</div>}
                        <div className="sa-plans-form-grid">
                            <div className="sa-plans-form-full">
                                <LabelWithInfo
                                    htmlFor="sp-name"
                                    label="Plan Name"
                                    tooltip="Display name shown to organizations on the Billing page."
                                />
                                <input
                                    id="sp-name"
                                    title="Plan Name"
                                    className="sa-plans-input"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="sa-plans-form-full">
                                <LabelWithInfo
                                    htmlFor="sp-desc"
                                    label="Description"
                                    tooltip="Marketing copy displayed below the plan name on the upgrade screen."
                                />
                                <textarea
                                    id="sp-desc"
                                    title="Description"
                                    className="sa-plans-textarea"
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                />
                            </div>
                            <div className="sa-plans-form-full">
                                <LabelWithInfo
                                    label="Organization (optional)"
                                    tooltip="Leave blank for a global plan available to all organizations. Select an org to make this plan exclusive to that organization."
                                />
                                <CustomDropdown
                                    value={form.org_id === '' ? '' : String(form.org_id)}
                                    options={[
                                        { value: '', label: '— Global (visible to all organizations) —' },
                                        ...orgOptions,
                                    ]}
                                    onChange={(v) => setForm({ ...form, org_id: v === '' ? '' : Number(v) })}
                                    placeholder="— Global (visible to all organizations) —"
                                />
                            </div>
                            <div>
                                <LabelWithInfo
                                    label="Plan Type"
                                    tooltip="Free plans are auto-assigned to new orgs. Paid plans appear on the Upgrade page."
                                />
                                <CustomDropdown
                                    value={form.plan_type}
                                    options={planTypeOptions}
                                    onChange={(v) => {
                                        const next = v as PlanType;
                                        setForm({
                                            ...form,
                                            plan_type: next,
                                            billing_interval: next === 'free' ? 'free' : (form.billing_interval === 'free' ? 'month' : form.billing_interval),
                                        });
                                    }}
                                />
                            </div>
                            {form.plan_type !== 'free' && (
                                <div>
                                    <LabelWithInfo
                                        label="Billing Interval"
                                        tooltip="How often Stripe charges the subscriber."
                                    />
                                    <CustomDropdown
                                        value={form.billing_interval}
                                        options={billingIntervalOptions}
                                        onChange={(v) => setForm({ ...form, billing_interval: v as BillingInterval })}
                                    />
                                </div>
                            )}
                            {form.plan_type === 'paid' && (
                                <>
                                    <div>
                                        <LabelWithInfo
                                            htmlFor="sp-price"
                                            label="Plan Price (USD)"
                                            tooltip="Charged each billing interval in US dollars. Platform-key LLM usage is funded by LLM Usage Credits."
                                        />
                                        <input
                                            id="sp-price"
                                            title="Plan Price (USD)"
                                            className="sa-plans-input"
                                            type="number" min="0" step="0.01"
                                            value={form.price_dollars}
                                            onChange={(e) => setForm({ ...form, price_dollars: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}
                            {form.plan_type === 'free' && (
                                <>
                                    <div>
                                        <LabelWithInfo
                                            htmlFor="sp-free-quota"
                                            label="Free Token Quota"
                                            tooltip="Tokens granted to a newly created organization on its free plan."
                                        />
                                        <input
                                            id="sp-free-quota"
                                            title="Free Token Quota"
                                            className="sa-plans-input"
                                            type="number" min="0" step="1"
                                            value={form.free_token_quota}
                                            onChange={(e) => setForm({ ...form, free_token_quota: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <LabelWithInfo
                                            htmlFor="sp-free-period"
                                            label="Free Quota Period (days)"
                                            tooltip="After this many days, the free trial expires and the org must subscribe to a paid plan to continue."
                                        />
                                        <input
                                            id="sp-free-period"
                                            title="Free Quota Period (days)"
                                            className="sa-plans-input"
                                            type="number" min="1" step="1"
                                            value={form.free_quota_period_days}
                                            onChange={(e) => setForm({ ...form, free_quota_period_days: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}
                            <div>
                                <LabelWithInfo
                                    htmlFor="sp-user-limit"
                                    label="User Limit"
                                    tooltip="Maximum number of seats. Leave blank for unlimited."
                                />
                                <input
                                    id="sp-user-limit"
                                    title="User Limit"
                                    className="sa-plans-input"
                                    type="number" min="1" step="1"
                                    value={form.user_limit}
                                    onChange={(e) => setForm({ ...form, user_limit: e.target.value })}
                                    placeholder="leave blank for unlimited"
                                />
                            </div>
                            <div className="sa-plans-form-full">
                                <LabelWithInfo
                                    label="Features Unlocked"
                                    tooltip="Toggle individual platform capabilities included in this plan."
                                />
                                <div className="sa-plans-feature-toggle-list">
                                    {FEATURE_OPTIONS.map((opt) => (
                                        <div
                                            key={opt.key}
                                            className={`sa-plans-toggle-row ${form.feature_flags.has(opt.key) || opt.locked ? 'sa-plans-toggle-row-active' : ''}`}
                                        >
                                            <div className="sa-plans-toggle-row-text">
                                                <span className="sa-plans-toggle-row-title">{opt.label}{opt.locked ? ' (always on)' : ''}</span>
                                                <span className="sa-plans-toggle-row-sub">{opt.tooltip}</span>
                                            </div>
                                            <PlanToggle
                                                checked={form.feature_flags.has(opt.key) || !!opt.locked}
                                                disabled={opt.locked}
                                                onChange={(v) => toggleFeature(opt.key, v)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className={`sa-plans-form-full sa-plans-toggle-row ${form.is_active ? 'sa-plans-toggle-row-active' : ''}`}>
                                <div className="sa-plans-toggle-row-text">
                                    <span className="sa-plans-toggle-row-title">Active</span>
                                    <span className="sa-plans-toggle-row-sub">Inactive plans are hidden from the org-admin Upgrade page.</span>
                                </div>
                                <PlanToggle
                                    checked={form.is_active}
                                    onChange={(v) => setForm({ ...form, is_active: v })}
                                />
                            </div>
                        </div>
                        <div className="sa-plans-modal-actions">
                            <button type="button" className="sa-plans-btn" onClick={() => setModalOpen(false)} disabled={submitting}>Cancel</button>
                            <button type="submit" className="sa-plans-btn sa-plans-btn-primary sa-plans-btn-icon" disabled={submitting}>
                                {submitting
                                    ? <><Spinner animation="border" size="sm" /> Saving…</>
                                    : editing
                                        ? <><PencilSquare size={14} /> Save changes</>
                                        : <><PlusLg size={14} /> Create plan</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default SuperAdminSubscriptionPlans;
